import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FadeIn from '../../components/ui/FadeIn';
import { useToast } from '../../components/Toast/ToastContext';
import { useConfirm } from '../../components/ConfirmModal/ConfirmContext';
import { Button, buttonClass } from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import Tooltip from '../../components/ui/Tooltip';
import useUserStore from '../../stores/useUserStore';
import * as api from '../api';
import { canArrowFrom, canEditNode, canEditNodeText, canRemoveEdge } from '../permissions';
import type { NodePatch, PlanNode, PlanResponse } from '../types';
import { useUnread } from '../useUnread';
import { doneCopy } from '../DocSection';
import type { PointsHost } from '../pointsTypes';
import { CanvasStoreContext, createCanvasStore, useCanvas } from './canvasStore';
import type { CanvasStoreBundle, NodeBox } from './canvasStore';
import CanvasGroup from './CanvasGroup';
import CanvasNode from './CanvasNode';
import ContextMenu from './ContextMenu';
import type { MenuRequest, MenuTarget } from './ContextMenu';
import Controls from './Controls';
import EdgeLayer, { ConnectionLine } from './EdgeLayer';
import { createEdge, deleteEdge } from '../api';
import { DRAG_THRESHOLD, swallowNextClick } from './events';
import { bezierPath, flowToScreen, handlePosition, layoutPositions, toSaved } from './geometry';
import { GROUP_DEFAULT, centerOf, childrenOf, clampGroupSize, depthsOf, descendantsIn, groupAround, groupAt, groupSizesOf, isGroup } from './groups';
import NodeSidePanel from './NodeSidePanel';
import { CanvasRuntimeContext } from './runtime';
import type { CanvasRuntime } from './runtime';
import { useConnect } from './useConnect';
import { useSelection } from './useSelection';
import { TIDY_WINDOW_MS, useTidy } from './useTidy';
import { useViewport } from './useViewport';
import './canvas.css';

export interface CanvasProps {
  planId: string;
  data: PlanResponse;
  canManage: boolean;
  /** False when the plan is locked or archived: every write would 409. */
  writable: boolean;
  reload: () => Promise<void>;
  /** Switch to the document view, scrolled to this node when one is given. */
  onOpenInDocument: (nodeId: string | null) => void;
  /** Lock or unlock the plan. Absent when this person cannot; the narrow HUD has no Lock key, so the pane menu carries it. */
  onToggleLock?: () => void;
  showDone: boolean;
  onShowDone: (on: boolean) => void;
}

const LONG_PRESS_MS = 500;
/** A keyboard-opened menu also fires the browser's contextmenu event; this long after, it is the same gesture. */
const SAME_GESTURE_MS = 800;

const bySortThenId = (a: PlanNode, b: PlanNode) =>
  a.sort !== b.sort ? a.sort - b.sort : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

let instances = 0;

const Marquee: React.FC = () => {
  const rect = useCanvas((s) => s.marquee);
  if (!rect) return null;
  return <div className="np-marquee" style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }} />;
};

const BAR_KEY = buttonClass('tonal', 'sm');

const EdgeSelectionBar: React.FC<{
  canRemove: (id: string) => boolean;
  onRemove: (ids: string[]) => void;
  onLabel: (id: string) => void;
}> = ({ canRemove, onRemove, onLabel }) => {
  const selected = useCanvas((s) => s.edgeSelection);
  const ids = Array.from(selected).filter(canRemove);
  if (ids.length === 0) return null;
  return (
    <div data-canvas-control className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-1.5">
      {ids.length === 1 && (
        <Tooltip label="Write a short label on this arrow">
          <button type="button" onClick={() => onLabel(ids[0])} className={BAR_KEY}>
            Label
          </button>
        </Tooltip>
      )}
      <button type="button" onClick={() => onRemove(ids)} className={BAR_KEY}>
        {ids.length === 1 ? 'Remove arrow' : `Remove ${ids.length} arrows`}
      </button>
    </div>
  );
};

const KEEP_KEY = buttonClass('filled', 'sm');
const UNDO_KEY = buttonClass('quiet', 'sm');

const TidyBar: React.FC<{ onKeep: () => void; onUndo: () => void }> = ({ onKeep, onUndo }) => (
  <div
    data-canvas-control
    data-tidy-bar
    className="absolute left-1/2 top-3 z-10 -translate-x-1/2 overflow-hidden rounded-full bg-surface-container-high shadow-lg"
  >
    <div className="flex items-center gap-1 py-1 pl-4 pr-1">
      <span role="status" className="mr-1 whitespace-nowrap text-xs text-on-surface">
        Tidied up.
      </span>
      <Tooltip label="Save these positions for everyone">
        <button type="button" onClick={onKeep} className={KEEP_KEY}>
          Keep
        </button>
      </Tooltip>
      <Tooltip label="Put every card back where it was">
        <button type="button" onClick={onUndo} className={UNDO_KEY}>
          Undo
        </button>
      </Tooltip>
    </div>
    <span aria-hidden="true" className="np-tidy-drain" style={{ animationDuration: `${TIDY_WINDOW_MS}ms` }} />
  </div>
);

/** Keys that only move focus or hold a modifier: pressing them is not using the canvas. */
const PASSIVE_KEYS = new Set(['Tab', 'Shift', 'Control', 'Alt', 'Meta']);
const inTidyBar = (t: EventTarget | null) => !!(t as HTMLElement | null)?.closest?.('[data-tidy-bar]');

const CanvasInner: React.FC<CanvasProps & { bundle: CanvasStoreBundle }> = ({
  planId,
  data,
  canManage,
  writable,
  reload,
  onOpenInDocument,
  onToggleLock,
  showDone,
  onShowDone,
  bundle,
}) => {
  const { store } = bundle;
  const toast = useToast();
  const confirm = useConfirm();
  const userId = useUserStore((s: any) => s.user?.id);
  const canEdit = canManage && writable;
  const { role } = data;

  const paneRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const patternRef = useRef<SVGPatternElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const [uid] = useState(() => `np-canvas-${(instances += 1)}`);
  const [focusToken, setFocusToken] = useState(0);
  // The panel opens on an explicit click, tap or Enter, never because a drag or marquee selected a node.
  const [panelFor, setPanelFor] = useState<string | null>(null);

  const nodes = useMemo(() => [...data.nodes].sort(bySortThenId), [data.nodes]);
  const nodesById = useMemo(() => new Map(data.nodes.map((n) => [n.id, n])), [data.nodes]);
  const numberOf = useMemo(() => new Map(data.tree.sections.map((s) => [s.id, s.number])), [data.tree.sections]);
  const titles = useMemo(() => new Map(data.nodes.map((n) => [n.id, n.title])), [data.nodes]);
  const positions = useMemo(() => layoutPositions(data.nodes, data.tree.sections), [data.nodes, data.tree.sections]);
  const groupSizes = useMemo(() => groupSizesOf(data.nodes), [data.nodes]);
  const children = useMemo(() => childrenOf(data.nodes), [data.nodes]);
  const depths = useMemo(() => depthsOf(data.nodes, data.tree.sections), [data.nodes, data.tree.sections]);
  // Parents before children: bubbles shallowest first, then every topic above them.
  const groups = useMemo(
    () => nodes.filter(isGroup).sort((a, b) => (depths.get(a.id) ?? 0) - (depths.get(b.id) ?? 0)),
    [nodes, depths],
  );
  const topics = useMemo(() => nodes.filter((n) => !isGroup(n)), [nodes]);

  // Per-node rights: a manager holds every node, an authority holds theirs.
  const mayEditNode = (n: PlanNode) => writable && canEditNode(n, role, userId);
  const mayRemoveEdge = (edgeId: string) => {
    const edge = data.edges.find((x) => x.id === edgeId);
    return !!edge && writable && canRemoveEdge(edge, nodesById, role, userId);
  };
  const holdsAny = canEdit || data.nodes.some(mayEditNode);
  // The gesture hooks and nodes are built once; they ask through these refs for today's answer.
  const rights = useRef({ mayEditNode, mayRemoveEdge, nodesById, children, depths });
  rights.current = { mayEditNode, mayRemoveEdge, nodesById, children, depths };
  const canMove = useCallback((id: string) => {
    const { nodesById: byId, mayEditNode: may, children: kids } = rights.current;
    const n = byId.get(id);
    if (!n || !may(n)) return false;
    // A group moves whole or not at all: an authority who cannot move all it holds would leave it behind.
    return descendantsIn(kids, id).every((d) => {
      const c = byId.get(d);
      return !!c && may(c);
    });
  }, []);
  const canRemoveEdgeId = useCallback((id: string) => rights.current.mayRemoveEdge(id), []);
  const descendantsOf = useCallback((id: string) => descendantsIn(rights.current.children, id), []);
  const parentOf = useCallback((id: string) => rights.current.nodesById.get(id)?.parent_id ?? null, []);
  const groupUnder = useCallback(
    (id: string) => {
      const all = store.getState().nodes;
      const b = all[id];
      if (!b?.measured) return null;
      const exclude = new Set([id, ...descendantsIn(rights.current.children, id)]);
      return groupAt(all, centerOf(b), rights.current.depths, exclude);
    },
    [store],
  );

  useLayoutEffect(() => {
    store.getState().syncPositions(positions, groupSizes);
  }, [store, positions, groupSizes]);

  useEffect(() => {
    store.getState().pruneEdges(new Set(data.edges.map((e) => e.id)));
  }, [store, data.edges]);

  // Callbacks the gesture hooks call back into. They read the newest props through a ref,
  // so the runtime below is built once and no node re-renders when the page does.
  const live = useRef({ planId, reload, toast });
  live.current = { planId, reload, toast };

  /** `parents`: nodes changing group on this drop, saved alongside the positions under the one reload. */
  const savePositions = useCallback(
    async (ids: string[], parents: Record<string, string | null> = {}) => {
      const { planId: pid, reload: again, toast: t } = live.current;
      const s = store.getState();
      const patches: Record<string, NodePatch> = {};
      const snapped: Record<string, { x: number; y: number }> = {};
      // A done topic whose group moves in this batch is carried by the server (a done
      // topic is on the client only while Show done is on), so it is moved here but not saved.
      const moving = new Set(ids);
      const { nodesById: byId } = rights.current;
      const ridesAlong = (id: string) => {
        if (!byId.get(id)?.done_at) return false;
        for (let p = byId.get(id)?.parent_id ?? null; p; p = byId.get(p)?.parent_id ?? null) {
          if (moving.has(p)) return true;
        }
        return false;
      };
      ids.forEach((id) => {
        const b = s.nodes[id];
        if (!b) return;
        const p = toSaved(b);
        snapped[id] = p;
        if (ridesAlong(id)) return;
        const was = bundle.serverPosition(id);
        if (!was || was.x !== p.x || was.y !== p.y) patches[id] = { x: p.x, y: p.y };
      });
      s.moveNodes(snapped);
      Object.keys(parents).forEach((id) => {
        patches[id] = { ...patches[id], parent_id: parents[id] };
      });
      const saves = Object.keys(patches);
      if (saves.length === 0) return;
      // One PATCH per node, position and group together, so the server takes both or neither. Then one reload.
      const results = await Promise.allSettled(saves.map((id) => api.updateNode(pid, id, patches[id])));
      const failed = saves.filter((_id, i) => results[i].status === 'rejected');
      if (failed.length > 0) {
        const first = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
        t.error(api.errorMessage(first?.reason, failed.length === 1 ? 'Could not save the new position.' : 'Could not save the new positions.'));
        const back: Record<string, { x: number; y: number }> = {};
        failed.forEach((id) => {
          const was = bundle.serverPosition(id);
          if (was) back[id] = was;
        });
        store.getState().moveNodes(back);
      }
      await again();
    },
    [store, bundle],
  );

  /** A drag ended: each dragged root takes the innermost group under its center, or none. */
  const finishDrag = useCallback(
    (ids: string[]) => {
      const moved = new Set(ids);
      const parents: Record<string, string | null> = {};
      ids.forEach((id) => {
        const n = rights.current.nodesById.get(id);
        // A node whose group moved with it keeps its group.
        if (!n || (n.parent_id && moved.has(n.parent_id)) || !canMove(id)) return;
        const target = groupUnder(id);
        if (target !== (n.parent_id ?? null)) parents[id] = target;
      });
      savePositions(ids, parents);
    },
    [canMove, groupUnder, savePositions],
  );

  const saveSize = useCallback(
    async (id: string) => {
      const { planId: pid, reload: again, toast: t } = live.current;
      const box = store.getState().nodes[id];
      if (!box) return;
      const size = clampGroupSize(box);
      const was = bundle.serverSize(id);
      store.getState().resizeNode(id, size);
      if (was && was.width === size.width && was.height === size.height) return;
      try {
        await api.updateNode(pid, id, size);
      } catch (e) {
        t.error(api.errorMessage(e, 'Could not save the new size.'));
        if (was) store.getState().resizeNode(id, was);
      }
      await again();
    },
    [store, bundle],
  );

  const deleteEdges = useCallback(
    async (ids: string[]) => {
      const { planId: pid, reload: again, toast: t } = live.current;
      const results = await Promise.allSettled(ids.map((id) => deleteEdge(pid, id)));
      const first = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (first) t.error(api.errorMessage(first.reason, 'Could not remove the arrow.'));
      store.getState().selectEdges([]);
      await again();
      // Focus sat on the arrow (or the bar) the reload just took away; keep it on the canvas.
      const active = document.activeElement;
      if (!active || active === document.body) paneRef.current?.focus({ preventScroll: true });
    },
    [store],
  );

  // ── Arrow labels: an inline field at the arrow's midpoint ──

  const [labeling, setLabeling] = useState<string | null>(null);
  const openLabel = useCallback((id: string) => setLabeling(id), []);
  const canLabel = useCallback((id: string) => rights.current.mayRemoveEdge(id), []);
  /** Back to the arrow once the field is gone, unless a click already put focus somewhere. */
  const refocusArrow = (id: string) =>
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const hit = paneRef.current?.querySelector<SVGElement>(`g[data-edge-id="${id}"] > .np-edge-hit`);
      (hit ?? paneRef.current)?.focus({ preventScroll: true });
    }, 0);
  const cancelLabel = () => {
    const id = labeling;
    setLabeling(null);
    if (id) refocusArrow(id);
  };

  useEffect(() => {
    if (labeling && !data.edges.some((e) => e.id === labeling)) setLabeling(null);
  }, [labeling, data.edges]);

  const connectNodes = useCallback(async (sourceId: string, targetId: string) => {
    const { planId: pid, reload: again, toast: t } = live.current;
    try {
      await createEdge(pid, { source_id: sourceId, target_id: targetId });
    } catch (e) {
      t.error(api.errorMessage(e, 'Could not draw the arrow.'));
    }
    // The document's flow order and After chips come from these edges, so the tree reloads too.
    await again();
  }, []);

  const viewport = useViewport(store, { pane: paneRef, layer: layerRef, pattern: patternRef, dot: dotRef });
  const selection = useSelection(bundle, viewport, {
    canMove,
    descendantsOf,
    canRemoveEdge: canRemoveEdgeId,
    onDeleteEdges: deleteEdges,
    onSavePositions: savePositions,
  });
  const connect = useConnect(bundle, viewport, {
    edges: data.edges,
    isProposed: (id) => nodesById.get(id)?.status === 'proposed',
    onConnect: connectNodes,
  });

  const tidy = useTidy(bundle, viewport, {
    graph: () => ({ nodes: data.nodes, edges: data.edges }),
    onKeep: savePositions,
    onAlreadyTidy: () => toast.info('Already tidy. Nothing needed to move.'),
  });
  // Any other use of the canvas while the Keep or Undo bar is up keeps the new layout.
  const settleTidy = (t: EventTarget | null) => {
    if (tidy.pending && !inTidyBar(t)) tidy.keep();
  };
  const { pending: tidyPending, keep: keepTidy } = tidy;
  useEffect(() => {
    const pane = paneRef.current;
    if (!tidyPending || !pane) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!inTidyBar(e.target)) keepTidy();
    };
    pane.addEventListener('wheel', onWheel, { passive: true });
    return () => pane.removeEventListener('wheel', onWheel);
  }, [tidyPending, keepTidy]);
  const closeTidy = (fn: () => void) => () => {
    fn();
    paneRef.current?.focus({ preventScroll: true });
  };

  // One ResizeObserver for every node, batched into one store write per callback.
  const observer = useRef<ResizeObserver | null>(null);
  const observed = useRef(new Map<string, HTMLElement>());
  const observe = useCallback(
    (id: string, el: HTMLElement | null) => {
      const prev = observed.current.get(id);
      if (prev && prev !== el) {
        observer.current?.unobserve(prev);
        observed.current.delete(id);
      }
      if (!el) return;
      observed.current.set(id, el);
      if (typeof ResizeObserver === 'undefined') {
        // No observer (the test renderer, very old browsers): measure once, now.
        store.getState().setSizes({ [id]: { width: el.offsetWidth, height: el.offsetHeight } });
        return;
      }
      if (!observer.current) {
        observer.current = new ResizeObserver((entries) => {
          const sizes: Record<string, { width: number; height: number }> = {};
          entries.forEach((entry) => {
            const node = entry.target as HTMLElement;
            const nid = node.getAttribute('data-node-id');
            // Trap 2, rect inside a scaled layer: offsetWidth/Height are the unscaled layout size.
            if (nid) sizes[nid] = { width: node.offsetWidth, height: node.offsetHeight };
          });
          store.getState().setSizes(sizes);
        });
      }
      observer.current.observe(el);
    },
    [store],
  );
  useEffect(() => () => observer.current?.disconnect(), []);

  // Trap 4, fitView before measure: the first fit waits until every node has a size.
  const fitQueued = useRef(true);
  useEffect(() => {
    const check = () => {
      if (!fitQueued.current) return;
      const all = store.getState().nodes;
      if (Object.keys(all).every((id) => all[id].measured)) {
        fitQueued.current = false;
        viewport.fitView(false);
      }
    };
    check();
    return store.subscribe(check);
  }, [store, viewport]);

  const openPanel = useCallback(
    (id: string, focus: boolean) => {
      store.getState().selectNodes([id]);
      setPanelFor(id);
      // Zero on a pointer open, so a panel that remounts never pulls focus off the canvas.
      setFocusToken((t) => (focus ? t + 1 : 0));
    },
    [store],
  );

  const runtime = useMemo<CanvasRuntime>(
    () => ({
      viewport,
      selection,
      connect,
      observe,
      savePositions,
      finishDrag,
      saveSize,
      descendantsOf,
      parentOf,
      groupUnder,
      canMove,
      openPanel,
    }),
    [viewport, selection, connect, observe, savePositions, finishDrag, saveSize, descendantsOf, parentOf, groupUnder, canMove, openPanel],
  );

  // ── The side panel's writes, the same calls the document view makes ──

  const run = async (fn: () => Promise<unknown>, fallback: string, success?: string): Promise<boolean> => {
    try {
      await fn();
      if (success) toast.success(success);
      await reload();
      return true;
    } catch (e) {
      toast.error(api.errorMessage(e, fallback));
      reload();
      return false;
    }
  };

  const saveLabel = async (edgeId: string, label: string): Promise<boolean> => {
    const edge = data.edges.find((x) => x.id === edgeId);
    if (edge && edge.label.trim() !== label) {
      const ok = await run(() => api.updateEdge(planId, edgeId, { label }), 'Could not save the label. Try again, or press Escape to leave it.');
      if (!ok) return false;
    }
    setLabeling(null);
    refocusArrow(edgeId);
    return true;
  };

  const { counts: unread, markSeen } = useUnread(planId);

  const soleSelected = useCanvas((s) => (s.selection.size === 1 ? s.selection.values().next().value ?? null : null)) as string | null;
  const panelNode = soleSelected && soleSelected === panelFor ? nodesById.get(soleSelected) : undefined;
  const panelId = panelNode?.id ?? null;
  const panelParent = panelNode?.parent_id ? nodesById.get(panelNode.parent_id) : undefined;
  const panelUnread = panelId ? unread[panelId] ?? 0 : 0;

  const pointsHost: PointsHost = {
    planId,
    role,
    userId: userId ?? null,
    locked: !!data.plan.locked_at,
    archived: !!data.plan.archived_at,
    onChanged: reload,
  };

  // An open side panel is a topic being read: its count clears.
  useEffect(() => {
    if (panelId && panelUnread > 0) markSeen(panelId);
  }, [panelId, panelUnread, markSeen]);

  // A reload can take the panel's node away (marked done with Show done off). The sync already
  // drops it from the selection; a stale panelFor would reopen the panel when it next comes back.
  useEffect(() => {
    if (!panelFor || nodesById.has(panelFor)) return;
    setPanelFor(null);
    const active = document.activeElement;
    if (!active || active === document.body) paneRef.current?.focus({ preventScroll: true });
  }, [panelFor, nodesById]);

  const closePanel = () => {
    const id = panelFor;
    setPanelFor(null);
    store.getState().clearSelection();
    if (id) paneRef.current?.querySelector<HTMLElement>(`[data-node-id="${id}"]`)?.focus();
  };

  const removeNode = async (node: PlanNode) => {
    if (isGroup(node)) {
      const ok = await confirm({
        title: 'Remove this group?',
        message: `"${node.title}" leaves the plan, along with the arrows to and from it. What it holds stays, one level up.`,
        confirmLabel: 'Remove group',
        tone: 'danger',
      });
      if (ok) await run(() => api.deleteNode(planId, node.id), 'Could not remove the group.', 'Group removed');
      return;
    }
    const ok = await confirm({
      title: 'Remove this topic?',
      message: `"${node.title}" leaves the plan, along with the arrows to and from it.`,
      confirmLabel: 'Remove topic',
      tone: 'danger',
    });
    if (ok) await run(() => api.deleteNode(planId, node.id), 'Could not remove the topic.', 'Topic removed');
  };

  const toggleDone = (node: PlanNode) => {
    const copy = doneCopy(!node.done_at, showDone);
    return run(() => api.updateNode(planId, node.id, { done: !node.done_at }), copy.fallback, copy.success);
  };

  // ── Groups from the menu: one at a point, one around the selection, leaving one ──

  /** The bubble under a menu point, so a node added there belongs to it, not just looks it. */
  const homeAt = (flow: { x: number; y: number }) => {
    const parent = groupAt(store.getState().nodes, flow, depths, new Set());
    return parent ? { parent_id: parent } : {};
  };

  const addGroupAt = async (title: string, flow: { x: number; y: number }): Promise<boolean> => {
    try {
      const { node: group } = await api.createNode(planId, { title, kind: 'group', ...toSaved(flow), ...GROUP_DEFAULT, ...homeAt(flow) });
      await reload();
      openPanel(group.id, true);
      return true;
    } catch (e) {
      toast.error(api.errorMessage(e, 'Could not add the group.'));
      return false;
    }
  };

  const groupSelection = async (title: string, ids: string[]): Promise<boolean> => {
    const all = store.getState().nodes;
    const rect = groupAround(ids.map((id) => all[id]).filter((b): b is NodeBox => !!b?.measured));
    if (!rect) return false;
    const picked = new Set(ids);
    // A node with any picked ancestor stays where it is: that ancestor carries it in.
    const roots = ids.filter((id) => {
      let at = nodesById.get(id)?.parent_id ?? null;
      for (let steps = 0; at && steps < nodesById.size; steps += 1) {
        if (picked.has(at)) return false;
        at = nodesById.get(at)?.parent_id ?? null;
      }
      return nodesById.has(id);
    });
    // Picked from inside one bubble, the new group nests in that bubble.
    const home = nodesById.get(roots[0])?.parent_id ?? null;
    const shared = roots.every((id) => (nodesById.get(id)?.parent_id ?? null) === home) ? home : null;
    let groupId: string;
    try {
      const { node: group } = await api.createNode(planId, {
        title,
        kind: 'group',
        ...toSaved(rect),
        width: rect.width,
        height: rect.height,
        ...(shared ? { parent_id: shared } : {}),
      });
      groupId = group.id;
    } catch (e) {
      toast.error(api.errorMessage(e, 'Could not make the group.'));
      return false;
    }
    // The position goes too: a node never placed (0,0 on the server) is laid out beside the
    // placed ones, and the new group just became placed, so it would land outside the bubble.
    const results = await Promise.allSettled(
      roots.map((id) => api.updateNode(planId, id, { parent_id: groupId, ...(all[id] ? toSaved(all[id]) : {}) })),
    );
    const refused = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (refused) toast.error(api.errorMessage(refused.reason, 'Made the group, but not everything moved into it. Drag the rest in.'));
    await reload();
    store.getState().selectNodes([groupId]);
    return true;
  };

  // Out of a nested group lands in the group around it, as removing a group lifts what it holds.
  const leaveGroup = (node: PlanNode) =>
    run(
      () => api.updateNode(planId, node.id, { parent_id: (node.parent_id && nodesById.get(node.parent_id)?.parent_id) || null }),
      'Could not take that out of its group.',
    );

  const rejectNode = async (node: PlanNode) => {
    const ok = await confirm({
      title: 'Reject this proposal?',
      message: `"${node.title}" is removed from the plan.`,
      confirmLabel: 'Reject',
      tone: 'danger',
    });
    if (ok) await run(() => api.rejectNode(planId, node.id), 'Could not reject the topic.');
  };

  // ── The context menu: right-click, the context-menu key or Shift+F10, or a long press ──

  const canContribute = data.role !== 'viewer' && writable;
  const [menu, setMenu] = useState<MenuRequest | null>(null);
  const lastMenuOpen = useRef(0);
  const longPress = useRef<{ timer: number; id: number; start: { x: number; y: number }; fired: boolean } | null>(null);

  const targetOf = (el: EventTarget | null): MenuTarget => {
    const t = el as Element | null;
    const nodeId = t?.closest?.('[data-node-id]')?.getAttribute('data-node-id');
    if (nodeId) return { kind: 'node', id: nodeId };
    const edgeId = t?.closest?.('[data-edge-id]')?.getAttribute('data-edge-id');
    if (edgeId) return { kind: 'edge', id: edgeId };
    return { kind: 'pane' };
  };

  /** `echo`: a keyboard or long-press open, which the browser may follow with its own contextmenu event. */
  const openMenu = (target: MenuTarget, clientX: number, clientY: number, echo = false) => {
    if (echo) lastMenuOpen.current = Date.now();
    const s = store.getState();
    // The thing right-clicked becomes the selection, without opening the side panel.
    if (target.kind === 'node' && !s.selection.has(target.id)) s.selectNodes([target.id]);
    if (target.kind === 'edge') s.selectEdges([target.id]);
    setMenu({
      target,
      selection: Array.from(store.getState().selection),
      at: { x: clientX, y: clientY },
      flow: viewport.clientToFlow(clientX, clientY),
      returnFocus: (document.activeElement as HTMLElement | null) ?? paneRef.current,
    });
  };

  const menuRef = useRef(menu);
  menuRef.current = menu;
  const closeMenu = useCallback((returnFocus: boolean) => {
    const m = menuRef.current;
    setMenu(null);
    // After the portal unmounts, so focus does not land on a node that is going away.
    if (m && returnFocus) {
      window.setTimeout(() => {
        const home = m.returnFocus?.isConnected ? m.returnFocus : paneRef.current;
        home?.focus({ preventScroll: true });
      }, 0);
    }
  }, []);

  const onContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only over the canvas: the side panel and the rest of the page keep the browser's menu.
    if (!paneRef.current?.contains(e.target as Node) || isControl(e.target)) return;
    e.preventDefault();
    if (Date.now() - lastMenuOpen.current < SAME_GESTURE_MS) return;
    const lp = longPress.current;
    if (lp && !lp.fired) {
      // Android sends its own contextmenu on a long press: take it, and retire our timer.
      window.clearTimeout(lp.timer);
      lp.fired = true;
    }
    openMenu(targetOf(e.target), e.clientX, e.clientY);
  };

  /** An arrow's menu, anchored at its midpoint. False while either end is unmeasured. */
  const openEdgeMenu = (edgeId: string): boolean => {
    const s = store.getState();
    const edge = data.edges.find((x) => x.id === edgeId);
    const a = edge && s.nodes[edge.source_id];
    const b = edge && s.nodes[edge.target_id];
    if (!edge || !a?.measured || !b?.measured || !paneRef.current) return false;
    const from = handlePosition(a, 'right');
    const to = handlePosition(b, 'left');
    const mid = bezierPath(from.x, from.y, to.x, to.y);
    const pane = paneRef.current.getBoundingClientRect();
    const p = flowToScreen({ x: mid.labelX, y: mid.labelY }, viewport.vp.current);
    openMenu({ kind: 'edge', id: edge.id }, pane.left + p.x, pane.top + p.y, true);
    return true;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!PASSIVE_KEYS.has(e.key)) settleTidy(e.target);
    const target = targetOf(e.target);
    // F2 renames, as it does elsewhere: on a focused arrow it opens the label field.
    if (e.key === 'F2' && target.kind === 'edge' && mayRemoveEdge(target.id)) {
      e.preventDefault();
      setLabeling(target.id);
      return;
    }
    const menuKey = e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10');
    // Enter on a focused arrow opens its menu; on a node, Enter opens the side panel instead.
    const edgeEnter = e.key === 'Enter' && target.kind === 'edge';
    if ((menuKey || edgeEnter) && paneRef.current?.contains(e.target as Node)) {
      e.preventDefault();
      const r = (e.target as Element).getBoundingClientRect();
      if (target.kind === 'node') {
        openMenu(target, r.left + 12, r.bottom + 4, true);
        return;
      }
      if (target.kind === 'edge' && openEdgeMenu(target.id)) return;
      // From the pane, the one selected arrow takes the menu.
      const s = store.getState();
      if (s.edgeSelection.size === 1 && openEdgeMenu(Array.from(s.edgeSelection)[0])) return;
      openMenu({ kind: 'pane' }, r.left + r.width / 2, r.top + r.height / 2, true);
      return;
    }
    selection.onKeyDown(e);
  };

  const cancelLongPress = () => {
    if (longPress.current) window.clearTimeout(longPress.current.timer);
    longPress.current = null;
  };
  useEffect(
    () => () => {
      if (longPress.current) window.clearTimeout(longPress.current.timer);
    },
    [],
  );

  const menuTarget = menu?.target;
  const menuNode = menuTarget?.kind === 'node' ? nodesById.get(menuTarget.id) : undefined;
  const menuEdge = menuTarget?.kind === 'edge' ? data.edges.find((x) => x.id === menuTarget.id) : undefined;
  // "Connect to" from the menu: every other accepted topic without an arrow from this one yet,
  // the same rules a drawn arrow meets (useConnect's isValidConnection). The menu's node is
  // always the source, so an authority gets it on the node they hold.
  const connectOptions =
    menuNode && writable && canArrowFrom(menuNode, role, userId) && menuNode.status !== 'proposed'
      ? nodes
          .filter(
            (n) =>
              n.id !== menuNode.id &&
              n.status !== 'proposed' &&
              !data.edges.some((x) => x.source_id === menuNode.id && x.target_id === n.id),
          )
          .map((n) => ({ id: n.id, title: n.title, number: numberOf.get(n.id) ?? undefined }))
      : undefined;
  const canEditText = (n: PlanNode) => writable && canEditNodeText(n, role, userId);
  const setAuthority = (nodeId: string, authorityId: string | null) =>
    run(() => api.updateNode(planId, nodeId, { authority_id: authorityId }), 'Could not set the authority.');
  const narrow = () => !!window.matchMedia?.('(max-width: 759px)').matches;

  // ── Pane pointer routing: controls keep theirs, Shift starts a marquee, the rest pans ──

  const isControl = (t: EventTarget) => !!(t as HTMLElement).closest?.('[data-canvas-control]');

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    settleTidy(e.target);
    if (isControl(e.target)) return;
    if (e.pointerType === 'touch') {
      if (longPress.current) cancelLongPress();
      else {
        // A long press with no movement opens the same menu a right-click would.
        const target = targetOf(e.target);
        const at = { x: e.clientX, y: e.clientY };
        const lp = { id: e.pointerId, start: at, fired: false, timer: 0 };
        lp.timer = window.setTimeout(() => {
          lp.fired = true;
          openMenu(target, at.x, at.y, true);
        }, LONG_PRESS_MS);
        longPress.current = lp;
      }
    }
    const hit = (e.target as HTMLElement).closest('[data-node-id]');
    // Edges and empty space are not focusable; the pane takes focus so Delete and Escape reach it.
    if (!hit) paneRef.current?.focus({ preventScroll: true });
    // The marquee starts on empty space or a bubble; Shift on a card is still a click.
    if ((!hit || hit.hasAttribute('data-group')) && selection.onPointerDown(e)) return;
    viewport.onPointerDown(e);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const lp = longPress.current;
    if (lp && !lp.fired && e.pointerId === lp.id && Math.hypot(e.clientX - lp.start.x, e.clientY - lp.start.y) >= DRAG_THRESHOLD) {
      cancelLongPress();
    }
    if (!selection.onPointerMove(e)) viewport.onPointerMove(e);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const lp = longPress.current;
    if (lp && e.pointerId === lp.id) {
      cancelLongPress();
      // The finger lifting after a long press is not a tap: no panel under the menu.
      if (lp.fired) swallowNextClick();
    }
    if (!selection.onPointerUp(e)) viewport.onPointerUp(e);
  };
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-node-id], [data-edge-id], [data-canvas-control]')) return;
    selection.clickPane();
  };

  const empty = nodes.length === 0;
  // Only bubbles left, every topic done and hidden: finished, not empty.
  const allDone = data.done_count > 0 && nodes.every(isGroup);
  // The layout has no notion of containers yet: it would scatter a bubble's cards out of it.
  const mayTidy = canEdit && !empty && groups.length === 0;

  // Fill from wherever the canvas starts to the bottom of the viewport. The header above
  // grows and shrinks (a long title, an edited description), so its size is watched too.
  const rootRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = () => setTop(Math.max(0, Math.round(root.getBoundingClientRect().top + window.scrollY)));
    measure();
    window.addEventListener('resize', measure);
    const above = root.previousElementSibling;
    const ro = typeof ResizeObserver !== 'undefined' && above ? new ResizeObserver(measure) : null;
    if (ro && above) ro.observe(above);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, []);

  // Tab onto a card or arrow outside the view and the browser scrolls these overflow:hidden
  // boxes to reveal it, which the viewport math (pane origin at scroll 0) never accounts for.
  useEffect(() => {
    const boxes = [paneRef.current, rootRef.current].filter((el): el is HTMLDivElement => !!el);
    const unscroll = (e: Event) => {
      const el = e.currentTarget as HTMLElement;
      if (el.scrollLeft !== 0) el.scrollLeft = 0;
      if (el.scrollTop !== 0) el.scrollTop = 0;
    };
    boxes.forEach((el) => el.addEventListener('scroll', unscroll));
    return () => boxes.forEach((el) => el.removeEventListener('scroll', unscroll));
  }, []);

  return (
    <CanvasRuntimeContext.Provider value={runtime}>
      <div
        ref={rootRef}
        className="np-canvas relative overflow-hidden"
        style={{ '--np-top': `${top}px` } as React.CSSProperties}
        onKeyDown={onKeyDown}
        onKeyUp={selection.onKeyUp}
        onContextMenu={onContextMenu}
      >
        <div
          ref={paneRef}
          tabIndex={-1}
          role="region"
          aria-label="Plan canvas"
          aria-describedby={`${uid}-help`}
          className="np-pane"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={onClick}
        >
          <svg className="np-bg" aria-hidden="true">
            <defs>
              <pattern ref={patternRef} id={`${uid}-dots`} patternUnits="userSpaceOnUse" width="20" height="20">
                <circle ref={dotRef} cx="10" cy="10" r="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#${uid}-dots)`} />
          </svg>

          <div ref={layerRef} className="np-layer">
            {groups.map((n) => (
              <CanvasGroup
                key={n.id}
                node={n}
                depth={depths.get(n.id) ?? 0}
                canEdit={canMove(n.id)}
                arrows={canEdit ? 'both' : mayEditNode(n) ? 'out' : 'none'}
                unread={unread[n.id] ?? 0}
              />
            ))}
            {topics.map((n) => (
              <CanvasNode
                key={n.id}
                node={n}
                canEdit={mayEditNode(n)}
                arrows={canEdit ? 'both' : mayEditNode(n) ? 'out' : 'none'}
                unread={unread[n.id] ?? 0}
              />
            ))}
            <EdgeLayer
              edges={data.edges}
              markerId={`${uid}-arrow`}
              titles={titles}
              labeling={labeling}
              canLabel={canLabel}
              onLabel={openLabel}
              onSaveLabel={saveLabel}
              onCancelLabel={cancelLabel}
            />
            <ConnectionLine markerId={`${uid}-arrow`} />
          </div>

          <Marquee />
          {holdsAny && !tidy.pending && (
            <FadeIn>
              <EdgeSelectionBar canRemove={mayRemoveEdge} onRemove={deleteEdges} onLabel={openLabel} />
            </FadeIn>
          )}
          <Controls onTidy={mayTidy ? tidy.start : undefined} />
          {/* After the controls, so Tab from Tidy up lands on Keep. */}
          {tidy.pending && (
            <FadeIn>
              <TidyBar onKeep={closeTidy(tidy.keep)} onUndo={closeTidy(tidy.undo)} />
            </FadeIn>
          )}

          <p
            id={`${uid}-help`}
            className="pointer-events-none absolute bottom-3 left-1/2 hidden max-w-[70%] -translate-x-1/2 text-center text-[11px] text-on-surface-variant [@media(hover:hover)]:block"
          >
            {holdsAny
              ? 'Drag a card to move it. Drag from a dot to draw an arrow. Shift-drag to select several. Right-click for more.'
              : 'Scroll or pinch to zoom, drag to pan. Click a card to read it. Right-click for more.'}
          </p>

          {(empty || allDone) && (
            <FadeIn key={allDone ? 'done' : 'empty'}>
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
              <div data-canvas-control className="pointer-events-auto max-w-sm">
                {allDone ? (
                  <EmptyState
                    title="Every topic is done."
                    action={<Button variant="quiet" size="sm" onClick={() => onShowDone(true)}>Show done</Button>}
                  />
                ) : (
                  <EmptyState
                    title="Nothing to arrange yet"
                    action={<Button variant="tonal" size="sm" onClick={() => onOpenInDocument(null)}>Go to the document</Button>}
                  >
                    {canContribute
                      ? 'Right-click or long-press anywhere on the canvas to add a topic there, or write them out in the document view.'
                      : 'Topics are added in the document view. Once there are some, they show up here.'}
                  </EmptyState>
                )}
              </div>
            </div>
            </FadeIn>
          )}
        </div>

        {panelNode && (
          <NodeSidePanel
            node={panelNode}
            number={numberOf.get(panelNode.id) ?? undefined}
            parentTitle={panelParent?.title}
            onOpenParent={panelParent ? () => openPanel(panelParent.id, true) : undefined}
            canEditNode={mayEditNode(panelNode)}
            canDecide={canEdit}
            canEditText={canEditText(panelNode)}
            canSetAuthority={canEdit}
            members={data.members}
            points={pointsHost}
            onSetAuthority={(authorityId) => setAuthority(panelNode.id, authorityId)}
            focusToken={focusToken}
            onSave={(patch: NodePatch) => run(() => api.updateNode(planId, panelNode.id, patch), 'Could not save the topic.')}
            onAccept={() => run(() => api.acceptNode(planId, panelNode.id), 'Could not accept the topic.', 'Topic accepted')}
            onReject={() => rejectNode(panelNode)}
            onToggleDone={() => toggleDone(panelNode)}
            onDelete={() => removeNode(panelNode)}
            onOpenInDocument={() => onOpenInDocument(panelNode.id)}
            onClose={closePanel}
          />
        )}

        {menu && (
          <ContextMenu
            request={menu}
            node={menuNode}
            edge={menuEdge}
            connectOptions={connectOptions}
            canEdit={canEdit}
            canContribute={canContribute}
            canEditText={canEditText}
            canEditNode={mayEditNode}
            canRemoveEdge={(e) => mayRemoveEdge(e.id)}
            members={data.members}
            locked={!!data.plan.locked_at}
            onClose={closeMenu}
            actions={{
              open: (id) => openPanel(id, true),
              save: (id, patch) => run(() => api.updateNode(planId, id, patch), 'Could not save the topic.'),
              accept: (id) => run(() => api.acceptNode(planId, id), 'Could not accept the topic.', 'Topic accepted'),
              reject: rejectNode,
              toggleDone,
              remove: removeNode,
              removeEdge: (id) => deleteEdges([id]),
              labelEdge: openLabel,
              connect: connectNodes,
              setAuthority,
              addAt: (title, flow) =>
                run(
                  // A member's proposal lands top level whatever it asks, so only a manager sends a group.
                  () => api.createNode(planId, { title, ...toSaved(flow), ...(canManage ? homeAt(flow) : {}) }),
                  canManage ? 'Could not add the topic.' : 'Could not propose the topic.',
                  canManage ? undefined : 'Proposed. It waits for the owner or a plan admin.',
                ),
              addGroupAt,
              groupSelection,
              leaveGroup,
              fit: () => viewport.fitView(true),
              tidy: mayTidy ? tidy.start : undefined,
              toggleLock: onToggleLock && narrow() ? onToggleLock : undefined,
            }}
          />
        )}
      </div>
    </CanvasRuntimeContext.Provider>
  );
};

/** The canvas view of a plan: position and arrows. Everything else stays in the document view and the side panel. */
const Canvas: React.FC<CanvasProps> = (props) => {
  // One store per mounted canvas, seeded before the first render so nodes never flash at (0,0).
  const [bundle] = useState(() => {
    const b = createCanvasStore();
    b.store.getState().syncPositions(layoutPositions(props.data.nodes, props.data.tree.sections), groupSizesOf(props.data.nodes));
    return b;
  });
  return (
    <CanvasStoreContext.Provider value={bundle}>
      <CanvasInner {...props} bundle={bundle} />
    </CanvasStoreContext.Provider>
  );
};

export default Canvas;
